import { useAnalytics } from 'app/context/Analytics';
import { PosthogEventName } from 'app/copied/constants/PosthogEventName';
import { useCallback } from 'react';

export enum FeedbackSentiment {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
}

export enum FeedbackType {
  Response = 'response_feedback',
  Search = 'search_result_feedback',
  CSAT = 'csat_feedback',
}

export enum UserQueryType {
  AiChat = 'ai_chat_query',
  Search = 'search_query',
}

export default function useFeedbackAnalytics() {
  const { captureEventWithActiveScreen } = useAnalytics();

  const captureSecondaryFeedbackSubmitted = useCallback(
    ({
      feedbackType,
      userQuery = '',
      userQueryType,
      resultType = '',
      secondaryReason,
      secondaryReasonComment,
      secondaryReasonOptions = [],
    }: {
      feedbackType: FeedbackType;
      userQuery?: string;
      userQueryType: UserQueryType;
      resultType?: string;
      secondaryReason: string;
      secondaryReasonComment: string;
      secondaryReasonOptions?: string[];
    }) => {
      captureEventWithActiveScreen(PosthogEventName.SecondaryFeedbackSubmitted, {
        feedbackType,
        userQuery,
        userQueryType,
        resultType,
        secondaryReason,
        secondaryReasonComment,
        secondaryReasonOptions,
      });
    },
    [captureEventWithActiveScreen]
  );

  const captureSecondaryFeedbackSkipped = useCallback(
    ({
      feedbackType,
      userQuery = '',
      userQueryType,
      resultType = '',
    }: {
      feedbackType: FeedbackType;
      userQuery?: string;
      userQueryType: UserQueryType;
      resultType?: string;
    }) => {
      captureEventWithActiveScreen(PosthogEventName.SecondaryFeedbackSkipped, {
        feedbackType,
        userQuery,
        userQueryType,
        resultType,
      });
    },
    [captureEventWithActiveScreen]
  );

  const captureFeedbackClicked = useCallback(
    ({
      feedbackType,
      userQuery = '',
      userQueryType,
      resultType = '',
      feedbackSentiment,
    }: {
      feedbackType: FeedbackType;
      userQuery?: string;
      userQueryType: UserQueryType;
      resultType?: string;
      feedbackSentiment: FeedbackSentiment;
    }) => {
      captureEventWithActiveScreen(PosthogEventName.FeedbackClicked, {
        feedbackType,
        userQuery,
        userQueryType,
        resultType,
        feedbackSentiment,
      });
    },
    [captureEventWithActiveScreen]
  );

  const captureFeedbackDisplayed = useCallback(
    ({
      feedbackType,
      userQuery,
      userQueryType,
      resultType,
    }: {
      feedbackType: FeedbackType;
      userQuery?: string;
      userQueryType: UserQueryType;
      resultType?: string;
    }) => {
      captureEventWithActiveScreen(PosthogEventName.FeedbackDisplayed, {
        feedbackType,
        userQuery,
        userQueryType,
        resultType,
      });
    },
    [captureEventWithActiveScreen]
  );

  return {
    captureSecondaryFeedbackSubmitted,
    captureSecondaryFeedbackSkipped,
    captureFeedbackClicked,
    captureFeedbackDisplayed,
  };
}
